import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { serverTimestamp } from '@angular/fire/firestore';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { intersects } from 'radash';
import { Observable, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks, getBibleBooksCanon } from '../../../models/books.bible.model';
import * as groupSelector from '../../../state/selectors/group.selector';
import {
  QuestionType,
  QuizQuestion,
} from '../../../models/question.quiz.model';
import { QuestionTypePipe } from '../../../pipes/questionType.pipe';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { BibleReferencePickerComponent } from '../../components/inputs/bible-reference-picker/bible-reference-picker.component';

@Component({
  selector: 'app-dlg-edit-question',
  standalone: true,
  imports: [
    BtnSubmitComponent,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    BibleReferencePickerComponent,
    QuestionTypePipe,
  ],
  templateUrl: './dlg-edit-question.component.html',
  styleUrl: './dlg-edit-question.component.css',
})
export class DlgEditQuestionComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgEditQuestionComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  toastr: ToastrService = inject(ToastrService);
  store: Store<AppState> = inject(Store<AppState>);
  quizService: QuizService = inject(QuizService);
  utilsService: UtilsService = inject(UtilsService);
  private logger = inject(NGXLogger);

  mainForm: FormGroup;

  groupId: string | null = this.data.groupId || null;
  question: QuizQuestion | null = this.data.question || null;

  loading$: Observable<boolean> = of(false);

  questionTypes = QuestionType;
  questionTypesList = Object.entries(QuestionType).map(([key, value]) => ({
    key,
    value,
  }));
  bibleBooks = ProtestantCanonBooks as Record<string, string>;

  getQuestionTypeKey(val: string | undefined | null): string {
    if (!val) return 'multipleChoice';
    const entry = Object.entries(QuestionType).find(
      ([key, value]) => key === val || value === val
    );
    return entry ? entry[0] : 'multipleChoice';
  }

  constructor() {
    this.mainForm = new FormGroup({
      questionText: new FormControl(this.question?.questionText || '', [
        Validators.required,
      ]),
      explanation: new FormControl(this.question?.explanation || ''),
      reference: new FormControl(this.question?.reference || null),
      difficulty: new FormControl(this.question?.difficulty || '', [
        Validators.required,
      ]),
      score: new FormControl(this.question?.score || '', [Validators.required]),
      simpleAnswer: new FormControl(this.question?.simpleAnswer || ''),
      correctOptionId: new FormControl(this.question?.correctOptionId || ''),
      questionType: new FormControl(
        this.getQuestionTypeKey(this.question?.questionType),
        [Validators.required],
      ),
      options: new FormArray(
        (this.question?.options || []).map(
          (opt) =>
            new FormGroup({
              id: new FormControl(opt.id),
              text: new FormControl(opt.text || '', [Validators.required]),
            }),
        ),
      ),
    });
  }
  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    if (this.groupId) {
      this.store.select(groupSelector.getGroupById(this.groupId))
        .subscribe((group) => {
          this.bibleBooks = getBibleBooksCanon(group?.bibleType);
        });
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
  }

  addOption() {
    var options = this.optionsArray;

    options.push(
      new FormGroup({
        id: new FormControl(uuidv4()),
        text: new FormControl('', [Validators.required]),
      }),
    );
    this.mainForm.patchValue({ options: options });
  }

  get optionsArray() {
    return this.mainForm.get('options') as FormArray;
  }

  removeOption(optionIndex: number) {
    var options = this.optionsArray;
    options.removeAt(optionIndex);
    this.mainForm.patchValue({ options: options });
  }

  setCorrectOption(optionId: string) {
    const currentId = this.mainForm.get('correctOptionId')?.value;
    const newId = currentId === optionId ? null : optionId;
    this.mainForm.get('correctOptionId')?.setValue(newId);
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onSubmit() {
    if (this.mainForm.invalid) {
      this.mainForm.markAllAsTouched();
      this.utilsService.onShowAlert(
        {
          code: 'VALIDATION_ERROR',
          title: 'Validation Error',
          message:
            'Please fill in all required fields and fix validation errors.',
        } as AppExceptionModel,
        false,
      );
      return;
    }
    this.loading$ = of(true);

    const questionData: QuizQuestion = {
      ...this.mainForm.value,
      groupId: this.groupId!,
      modified: serverTimestamp(),
    };

    if ((questionData.questionType as any) === 'multipleChoice') {
      if ((questionData.options ?? []).length < 4) {
        this.utilsService.onShowAlert(
          {
            code: 'VALIDATION_ERROR',
            title: 'Validation Error',
            message:
              'For multiple choice questions, please provide at least 4 answer options.',
          } as AppExceptionModel,
          false,
        );
        this.loading$ = of(false);
        return;
      }

      if (
        !questionData.correctOptionId ||
        questionData.correctOptionId === ''
      ) {
        this.utilsService.onShowAlert(
          {
            code: 'VALIDATION_ERROR',
            title: 'Validation Error',
            message:
              'For multiple choice questions, please select a correct answer.',
          } as AppExceptionModel,
          false,
        );
        this.loading$ = of(false);
        return;
      }
    }

    if (
      intersects(
        [questionData.questionType as any],
        [
          'trueFalse',
          'shortAnswer',
          'fillInTheBlank',
        ],
      )
    ) {
      if (!questionData.simpleAnswer || questionData.simpleAnswer === '') {
        this.utilsService.onShowAlert(
          {
            code: 'VALIDATION_ERROR',
            title: 'Validation Error',
            message:
              'For true/false, short answer, and fill in the blank questions, please provide an answer.',
          } as AppExceptionModel,
          false,
        );
        this.loading$ = of(false);
        return;
      }
      questionData.options = undefined;
      delete questionData.correctOptionId;
    }

    this.quizService
      .updateQuestion({
        groupId: this.groupId!,
        questionId: this.question?.id!,
        question: questionData,
      })
      .then(() => {
        this.toastr.success('Question updated successfully');
        this.loading$ = of(false);
        this.dialogRef.close({
          ...questionData,
          id: this.question?.id!,
          modified: new Date(),
        });
      })
      .catch((error) => {
        this.utilsService.onShowAlert(
          {
            code: 'SAVE_QUESTION_ERROR',
            title: 'Save Failed',
            message:
              'An error occurred while saving questions. Please try again. Error details: ' +
              error.message,
          } as AppExceptionModel,
          false,
        );
        this.loading$ = of(false);
        this.logger.error('Error saving questions: ', error);
      });
  }
}
